/* InsCipher's stamping wording and state notes arrive as HTML. Rather than strip the known-bad parts
   out of it, this rebuilds it from an allowlist: text formatting and lists survive, links survive only
   with an http(s) or mailto target, and everything else — scripts, styles, frames, forms, every
   attribute but a link's href — is dropped. DOMParser builds an inert document, so nothing in the
   input runs while it is read. */

const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

// Removed together with everything inside them; any other unknown tag is unwrapped to its children.
const DROPPED_TAGS: ReadonlySet<string> = new Set([
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'noscript',
  'template',
  'svg',
  'math',
  'title',
]);

// eslint-disable-next-line no-control-regex
const CONTROL_AND_SPACE = /[\u0000-\u0020]/g;

/* The URL as given when it is safe to put in an href (http, https or mailto), else null. Leading
   whitespace and control characters are ignored when reading the scheme, the way browsers do. */
export function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  const scheme = trimmed
    .replace(CONTROL_AND_SPACE, '')
    .match(/^([a-z][a-z0-9+.-]*):/i)?.[1]
    ?.toLowerCase();
  if (scheme === 'http' || scheme === 'https' || scheme === 'mailto') return trimmed;
  return null;
}

function cleanChildren(source: Node, target: Node, doc: Document): void {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return; // comments, processing instructions

    const element = child as Element;
    const tag = element.tagName.toLowerCase();
    if (DROPPED_TAGS.has(tag)) return;
    if (!ALLOWED_TAGS.has(tag)) {
      cleanChildren(element, target, doc);
      return;
    }

    const copy = doc.createElement(tag);
    if (tag === 'a') {
      const href = safeUrl(element.getAttribute('href'));
      if (href) {
        copy.setAttribute('href', href);
        copy.setAttribute('target', '_blank');
        copy.setAttribute('rel', 'noopener noreferrer');
      }
    }
    cleanChildren(element, copy, doc);
    target.appendChild(copy);
  });
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const out = document.implementation.createHTMLDocument('');
  const container = out.createElement('div');
  cleanChildren(parsed.body, container, out);
  return container.innerHTML;
}
